import {
  useQuery,
  useMutation,
  QueryKey,
  useQueryClient,
} from "@tanstack/react-query";

import { unauthorize } from "containers/Auth/actions";
import { activeOrganizationIdSelector } from "containers/Auth/selectors";
import { HTTP_STATUS } from "containers/Request/constants";
import { createError } from "containers/Request/error";
import { config } from "src/configureStore";
import { ApiResponse } from "types/api";
import { dataFormatter } from "utils/jsonApi";
import { addQueryParamsToUrl } from "utils/url";

import { createRequest } from "../requests";
import { METHOD_NAMES } from "./constant";
import { tivoAppsEndpoints } from "./endpoints/tivoApps";
import {
  QueryConfig,
  MutationConfig,
  MutationVariables,
  RequestConfig,
  Endpoint,
  QueryEndpointClient,
  MutationEndpointClient,
  RouteMap,
  ApiClient,
} from "./types";

const isQueryConfig = (value: unknown): value is QueryConfig => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const queryConfig = value as QueryConfig;
  return (
    "queryOptions" in queryConfig ||
    "abortSignal" in queryConfig ||
    "requestOptions" in queryConfig ||
    "queryParams" in queryConfig
  );
};

const createQueryKey = (
  method: METHOD_NAMES,
  path: string,
  args: unknown[]
): QueryKey => ["api", method, path, ...args];

const getEndpointQueryKey = <TArgs extends unknown[]>(
  endpoint: Endpoint<TArgs>,
  url: string,
  endpointArgs: TArgs,
  queryParams?: RequestConfig["queryParams"]
): QueryKey => {
  if (endpoint.getQueryKey) {
    return endpoint.getQueryKey(endpointArgs, queryParams);
  }

  return createQueryKey(endpoint.method, url, [...endpointArgs, queryParams]);
};

const executeEndpoint = async <T>(
  endpoint: string,
  method: METHOD_NAMES,
  configOptions?: RequestConfig,
  body?: unknown
): Promise<T> => {
  let result: unknown = {};
  let url = endpoint;

  if (configOptions?.queryParams) {
    url = addQueryParamsToUrl(url, configOptions.queryParams);
  }

  const requestOptions = { ...configOptions?.requestOptions };
  if (body !== undefined) {
    requestOptions.body = JSON.stringify(body);
  }

  try {
    const state = config.store.getState();
    const activeOrganizationId = activeOrganizationIdSelector(state);

    const response = await createRequest(method, {
      activeOrganizationId,
      url,
      abortSignal: configOptions?.abortSignal,
      options: requestOptions,
    });

    if (!response.ok) {
      if (response.status === HTTP_STATUS.UNAUTHORIZED_401) {
        config.store.dispatch(unauthorize());
        throw { error: response.statusText, responseStatus: response.status };
      }

      if (
        response.status === HTTP_STATUS.INTERNAL_SERVER_ERROR_500 ||
        response.status === HTTP_STATUS.NOT_FOUND_404
      ) {
        throw { error: response.statusText, responseStatus: response.status };
      }

      let responseError: unknown = response.statusText;
      try {
        const responseResult = await response.json();
        responseError = responseResult.errors;
      } catch {
        responseError = response.statusText;
      }

      throw { error: responseError, responseStatus: response.status };
    }

    if (response.status !== HTTP_STATUS.NO_CONTENT_204) {
      result = await response.json();
    }

    const resultBody = result as Record<string, unknown>;
    result = {
      results: dataFormatter.deserialize(resultBody),
      meta: resultBody.meta,
    };
  } catch (error: unknown) {
    const typedError = error as { error?: unknown; responseStatus?: number };
    console.error(error);
    throw createError(typedError.error, typedError.responseStatus);
  }

  return result as T;
};

const createQueryEndpointClient = <TArgs extends unknown[]>(
  endpoint: Endpoint<TArgs>
): QueryEndpointClient<TArgs> => {
  const splitArgsAndConfig = <TData>(
    argsWithConfig: [...TArgs, QueryConfig<TData>?]
  ) => {
    const maybeConfig = argsWithConfig.at(-1);
    const hasConfig = isQueryConfig(maybeConfig);
    const endpointArgs = (
      hasConfig ? argsWithConfig.slice(0, -1) : argsWithConfig
    ) as TArgs;

    return {
      endpointArgs,
      configOptions: hasConfig
        ? (maybeConfig as QueryConfig<TData>)
        : undefined,
    };
  };

  return {
    useQuery: <TData>(
      ...argsWithConfig: [...TArgs, QueryConfig<ApiResponse<TData>>?]
    ) => {
      const queryClient = useQueryClient();
      const { endpointArgs, configOptions } =
        splitArgsAndConfig<ApiResponse<TData>>(argsWithConfig);
      const url = endpoint.path(...endpointArgs);
      const queryKey = getEndpointQueryKey(
        endpoint,
        url,
        endpointArgs,
        configOptions?.queryParams
      );
      const queryOptions = configOptions?.queryOptions ?? {};
      const defaultInitialData: ApiResponse<TData> | undefined =
        queryClient.getQueryData<ApiResponse<TData>>(queryKey);

      return useQuery<ApiResponse<TData>, Error, ApiResponse<TData>, QueryKey>({
        queryKey,
        queryFn: async () => {
          const response = await executeEndpoint<ApiResponse<TData>>(
            url,
            endpoint.method,
            configOptions
          );

          const transformedResponse: ApiResponse<TData> =
            endpoint.transformResponse
              ? (endpoint.transformResponse(response, {
                  args: endpointArgs,
                  queryParams: configOptions?.queryParams,
                }) as ApiResponse<TData>)
              : response;

          endpoint.hydrateQueryCache?.({
            queryClient,
            data: transformedResponse,
            args: endpointArgs,
            queryParams: configOptions?.queryParams,
            queryKey,
          });

          return transformedResponse;
        },
        staleTime: 60_000,
        initialData: defaultInitialData,
        ...queryOptions,
      });
    },
  };
};

const createMutationEndpointClient = <TArgs extends unknown[]>(
  endpoint: Endpoint<TArgs>
): MutationEndpointClient<TArgs> => {
  return {
    useMutation: <TData = unknown, TBody = unknown>(
      mutationConfig?: MutationConfig<ApiResponse<TData>, TBody>
    ) => {
      const queryClient = useQueryClient();
      const mutationOptions = mutationConfig?.mutationOptions ?? {};
      const mergedMutationOptions = { ...mutationOptions };

      // Add query invalidation to passed onSuccess
      mergedMutationOptions.onSuccess = async (data, variables, context) => {
        if (endpoint.invalidateQueries) {
          const queryKeys = endpoint.invalidateQueries(variables.args as TArgs);
          await Promise.all(
            queryKeys.map((queryKey) =>
              queryClient.invalidateQueries({ queryKey })
            )
          );
        }

        await mutationOptions.onSuccess?.(data, variables, context);
      };

      return useMutation<
        ApiResponse<TData>,
        Error,
        MutationVariables<TArgs, TBody>
      >({
        mutationFn: (variables: MutationVariables<TArgs, TBody>) => {
          const url = endpoint.path(...variables.args);
          return executeEndpoint<ApiResponse<TData>>(
            url,
            endpoint.method,
            mutationConfig,
            variables.body
          );
        },
        ...mergedMutationOptions,
      });
    },
  };
};

const createApiClient = <TRouter extends RouteMap>(
  router: TRouter
): ApiClient<TRouter> => {
  const client = {} as ApiClient<TRouter>;
  const endpointKeys = Object.keys(router) as (keyof TRouter)[];

  endpointKeys.forEach((key) => {
    const endpoint = router[key];
    (client as Record<keyof TRouter, unknown>)[key] =
      endpoint.method === METHOD_NAMES.GET
        ? createQueryEndpointClient(endpoint)
        : createMutationEndpointClient(endpoint);
  });

  return client;
};

export const tivoAppsApi = createApiClient(tivoAppsEndpoints);
